/**
 * ConversationPanel — HELPDESK-CONVERSATIONAL-WORKER-001.
 *
 * Chat surface for a technician<->Digital Employee Conversation. Reuses the existing
 * AgentPermissionsContext gating (agents_view/agents_run/agents_manage — the SAME grants
 * AgentRunPanel already uses) and the existing agentRunsApi.approve/reject for a task-assignment
 * turn awaiting approval — this component never implements its own approval flow.
 *
 * HUMAN_RESPONSE is the only text shown by default. TECHNICAL_TRACE (structured, sanitized —
 * never model reasoning) is rendered only inside a collapsed, agents_manage-gated details box.
 * The service stores that trace as a sibling row rather than a field of the reply, so the two are
 * paired at read time by conversationTracePairing before anything is rendered.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { conversationsApi, agentRunsApi, agentsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { agentsForTicket, usableAgentId } from "../utils/agentScope";
import EmployeeAvatar from "./EmployeeAvatar";
import { markConversationsRead, maxSequenceNo } from "./conversationReadState";
import { pairConversationTraces } from "./conversationTracePairing";
import { degradedAnalysisLabel } from "./degradedAnalysisVocabulary";
import { TRACE_EVIDENCE_SOURCE_NONE, traceEvidenceSourceLabel } from "./evidenceSourceVocabulary";
import { traceErrorLabel } from "./errorReasonVocabulary";
import { handoffSourceLabel } from "./handoffSourceVocabulary";
import { traceToolLabel } from "./toolNameVocabulary";

const TURN_TYPES = [
  { value: "direct_to_agent", label: "שיחה ישירה עם הסוכן" },
  { value: "assign_task_to_agent", label: "הקצאת משימה לסוכן" },
  { value: "ask_manager_and_route", label: "ניתוב דרך המנהל" },
];

const POLL_MS = 4000;

// Keep the failed step visible: a thread may have been created even when posting fails.
// Only server-provided safe details or HTTP status are shown; never raw request/error objects.
function conversationRequestError(err, step) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  // Do not reflect raw 5xx exception text to the browser; the provider's
  // controlled 503 is the only server-error detail we display.
  if (typeof detail === "string" && detail.trim()
      && detail.length <= 300 && (status === 503 || (status >= 400 && status < 500))) {
    return `${step}: ${detail.trim()}`;
  }
  if (typeof status === "number") {
    return `${step}: ${status >= 500 ? "שגיאת שרת" : "הבקשה נדחתה"} (HTTP ${status}).`;
  }
  return `${step}: לא התקבלה תשובה מהשרת. בדוק את החיבור ונסה שוב.`;
}
const AWAITING_STAGES = new Set(["awaiting_technician_review", "awaiting_approval"]);
const TERMINAL_STAGES = new Set(["completed", "resolved", "failed", "rejected"]);
const STAGE_LABELS = {
  pending: "ממתין להתחלה", analyzing: "מנתח", diagnosing: "מאבחן", executing: "מבצע", approved: "אושר",
  awaiting_technician_review: "ממתין לסקירת טכנאי", awaiting_approval: "ממתין לאישור",
  completed: "הושלם", resolved: "נפתר", failed: "נכשל", rejected: "נדחה", paused: "מושהה",
};

const TRACE_STATUS_LABELS = {
  SUCCEEDED: "הושלם",
  FAILED: "נכשל",
  PROPOSED: "ממתין לביצוע",
  APPROVED: "אושר",
};

// The same enum AnalysisOutput.degraded (and AgentRunDetailPage / ApprovalDecisionBrief) use: the
// reply or run this trace belongs to is NOT a real model answer. Drawn here so the fact is visible
// in the trace box itself and not only in the bubble's own text. The labels are shared with the
// ticket screen's own degraded disclosure so one run cannot be described two ways.

// The routing row names both ends of a handoff, and the second end is the one that used to arrive
// as a database key. `handoff.to` is the target employee's agent id — conversation_service stores
// `str(agent.id)` on both of its handoff paths — so the row drew a raw UUID where a coworker's name
// belonged. `handoff.to_name` is the name the manager's routing path stores beside it, but the
// explicit operator handoff (`handoff_conversation`) stores no name at all, which is why the live
// participant row is read first: it is the precedence handoffCenterModel's own handoffEmployeeName
// already applies, and the stored name is the fallback for a conversation whose participants did
// not load. When neither source can name the target the row draws no name rather than the id — the
// handoff notice above the box already spells out who it went to.
function handoffTargetName(handoff, participants) {
  if (!handoff?.to) return null;
  const toAgentId = String(handoff.to);
  const target = (participants || []).find((p) => p.agent_id && String(p.agent_id) === toAgentId);
  return target?.agent_name || handoff.to_name || null;
}

function TechnicalTrace({ trace, participants }) {
  const [open, setOpen] = useState(false);
  if (!trace) return null;
  const handoffTarget = handoffTargetName(trace.handoff, participants);
  return (
    <div className="mt-1 text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-gray-400 hover:text-gray-600 underline decoration-dotted"
      >
        {open ? "הסתר פרטים טכניים" : "הצג פרטים טכניים"}
      </button>
      {open && (
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 bg-gray-50 border border-gray-200 rounded p-2 text-gray-600">
          {trace.degraded && (
            <>
              <dt className="font-medium text-amber-700">אמינות:</dt>
              <dd className="text-amber-700" data-testid="trace-degraded">
                {degradedAnalysisLabel(trace.degraded)}
              </dd>
            </>
          )}
          <dt className="font-medium">כלי:</dt>
          <dd data-testid="trace-tool">{traceToolLabel(trace.tool)}</dd>
          <dt className="font-medium">סטטוס:</dt><dd>{TRACE_STATUS_LABELS[trace.action_status] || trace.action_status}</dd>
          {trace.evidence_source && trace.evidence_source !== TRACE_EVIDENCE_SOURCE_NONE && (
            <>
              <dt className="font-medium">מקור מידע:</dt>
              <dd data-testid="trace-evidence-source">{traceEvidenceSourceLabel(trace.evidence_source)}</dd>
            </>
          )}
          {trace.evidence_summary && (
            <>
              <dt className="font-medium">תמצית:</dt><dd className="break-words">{trace.evidence_summary}</dd>
            </>
          )}
          {trace.handoff && (
            <>
              <dt className="font-medium">ניתוב:</dt>
              <dd data-testid="trace-handoff">
                {`${handoffSourceLabel(trace.handoff.from)}${handoffTarget ? ` ← ${handoffTarget}` : ""}`
                  + `${trace.handoff.reason ? ` (${trace.handoff.reason})` : ""}`}
              </dd>
            </>
          )}
          {trace.error && (
            <>
              <dt className="font-medium text-red-500">שגיאה:</dt>
              <dd className="text-red-500" data-testid="trace-error">{traceErrorLabel(trace.error)}</dd>
            </>
          )}
          {trace.agent_run_id && (
            <>
              <dt className="font-medium">ריצת AI:</dt>
              <dd>
                <a href={`/agent-runs/${trace.agent_run_id}`} className="text-blue-600 hover:underline">
                  צפה בריצה
                </a>
              </dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}

function EmployeeHeader({ author }) {
  // An AGENT participant carries the employee's identity (EMPLOYEE-IDENTITY-PERSONA-001); anything
  // else (a legacy row, the manager) falls back to the generic label.
  if (author?.role === "agent" && author.agent_name) {
    return (
      <div className="flex items-center gap-1.5 text-xs font-semibold text-violet-700 mb-1">
        <EmployeeAvatar
          name={author.agent_name}
          avatarKey={author.agent_avatar_key}
          avatarColor={author.agent_avatar_color}
          size="sm"
        />
        <span>{author.agent_name}</span>
        {author.agent_role_title && <span className="font-normal text-violet-500">· {author.agent_role_title}</span>}
      </div>
    );
  }
  if (author?.role === "manager") {
    return (
      <div className="flex items-center gap-1 text-xs font-semibold text-violet-700 mb-1">
        <span>🧭</span><span>מנהל הצוות</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-xs font-semibold text-violet-700 mb-1">
      <span>🤖</span><span>סוכן AI</span>
    </div>
  );
}

function MessageBubble({ message, canManage, author, trace, participants }) {
  const type = message.message_type;
  if (type === "operator_message") {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900 max-w-[85%] mr-auto">
        <div className="whitespace-pre-wrap leading-relaxed">{message.content}</div>
      </div>
    );
  }
  if (type === "human_response" || (type === "system" && author?.role === "agent") || (type === "system" && author?.role === "manager")) {
    return (
      <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm text-violet-900 max-w-[85%] ml-auto">
        <EmployeeHeader author={author} />
        <div className={`whitespace-pre-wrap leading-relaxed ${type === "system" ? "italic" : ""}`}>{message.content}</div>
        {canManage && <TechnicalTrace trace={trace} participants={participants} />}
      </div>
    );
  }
  if (type === "handoff_notice") {
    return (
      <div className="text-xs text-gray-500 text-center border-t border-dashed border-gray-200 pt-2 mt-1">
        ↪ {message.content}
        {canManage && <TechnicalTrace trace={trace} participants={participants} />}
      </div>
    );
  }
  if (type === "technical_trace") {
    // Never a bubble of its own — pairConversationTraces folds it into the row above. Guarded here
    // too so a trace can never leak into the primary conversation as an empty message.
    return null;
  }
  return (
    <div className="text-xs text-gray-400 text-center italic">{message.content}</div>
  );
}

export default function ConversationPanel({
  conversationId: initialConversationId = null,
  ticketId = null,
  ticketCompanyId = null,
  customerId = null,
  deviceIdentifier = null,
  agentId: fixedAgentId = null,
  defaultAgentId = null,
  initialDraft = "",
  onConversationCreated,
}) {
  const { can: canAgent } = useAgentPermissions();
  const [conversationId, setConversationId] = useState(initialConversationId);
  const [messages, setMessages] = useState([]);
  const [turnType, setTurnType] = useState("direct_to_agent");
  const [draft, setDraft] = useState(initialDraft);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  // null is the sentinel for "the operator has not picked": it lets defaultAgentId speak until an
  // explicit choice (including an explicit clear) is made. AgentRunPanel's picker uses the same one.
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // Opening a thread records its read cursor for the Conversation workspace — once per discussion
  // per mount, so live polling while it is open never re-marks newer messages as read.
  const readMarkedRef = useRef(new Set());

  const canView = canAgent("agents_view");
  const canRun = canAgent("agents_run");
  const canManage = canAgent("agents_manage");
  const canApprove = canAgent("agents_approve");

  // No fixed agent (e.g. mounted from Ticket Detail rather than a specific Agent's own page) and
  // no conversation started yet — offer a picker, scoped to the ticket's own customer the same way
  // AgentRunPanel already scopes its own agent picker.
  useEffect(() => {
    if (fixedAgentId || conversationId) return;
    let cancelled = false;
    agentsApi.list().then((data) => {
      if (!cancelled) setAgents((data || []).filter((a) => a.is_enabled));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [fixedAgentId, conversationId]);

  const selectableAgents = fixedAgentId ? [] : agentsForTicket(agents, ticketCompanyId);
  // The ticket's own Digital Employee binding reaches the composer through defaultAgentId, the same
  // way it already reaches AgentRunPanel's picker: assigning an employee has to route the
  // conversation with that employee, not only the diagnosis run beside it. `??` and not `||` keeps
  // an explicit empty choice ("") winning over the default, and a default the picker does not offer
  // (stale, another customer's, or disabled) is dropped by usableAgentId instead of being sent to a
  // start the backend would refuse.
  const effectiveAgentId = fixedAgentId || usableAgentId(selectableAgents, selectedAgentId ?? defaultAgentId);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    try {
      const [data, conversation] = await Promise.all([
        conversationsApi.listMessages(id),
        conversationsApi.get(id),
      ]);
      setMessages(data);
      setParticipants(conversation?.participants || []);
      if (!readMarkedRef.current.has(id)) {
        readMarkedRef.current.add(id);
        markConversationsRead([{ id, lastSeenSeq: maxSequenceNo(data) }]);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "שגיאה בטעינת השיחה");
    }
  }, []);

  useEffect(() => {
    if (conversationId) loadMessages(conversationId);
  }, [conversationId, loadMessages]);

  const ensureConversation = useCallback(async () => {
    if (conversationId) return conversationId;
    const target = turnType === "ask_manager_and_route" || !effectiveAgentId
      ? { kind: "manager" }
      : { kind: "agent", agent_id: effectiveAgentId };
    const conversation = await conversationsApi.create({
      ticket_id: ticketId || undefined,
      customer_id: customerId || undefined,
      device_identifier: deviceIdentifier || undefined,
      target,
    });
    setConversationId(conversation.id);
    onConversationCreated?.(conversation);
    return conversation.id;
  }, [conversationId, ticketId, customerId, deviceIdentifier, effectiveAgentId, turnType, onConversationCreated]);

  const handleSend = useCallback(async () => {
    if (!draft.trim()) return;
    if (!conversationId && turnType !== "ask_manager_and_route" && !effectiveAgentId) {
      setError("יש לבחור סוכן, או לבחור ניתוב דרך המנהל");
      return;
    }
    setLoading(true);
    setError(null);
    let step = "יצירת השיחה";
    try {
      const id = await ensureConversation();
      step = "שליחת ההודעה";
      await conversationsApi.postMessage(id, { turn_type: turnType, content: draft.trim() });
      setDraft("");
      await loadMessages(id);
    } catch (err) {
      setError(conversationRequestError(err, step));
    } finally {
      setLoading(false);
    }
  }, [draft, turnType, conversationId, effectiveAgentId, ensureConversation, loadMessages]);

  const runAction = useCallback(async (fn, failMessage) => {
    setLoading(true);
    setError(null);
    try {
      await fn();
      await loadMessages(conversationId);
    } catch (err) {
      setError(err?.response?.data?.detail || failMessage);
    } finally {
      setLoading(false);
    }
  }, [conversationId, loadMessages]);

  // All three go through the EXISTING run endpoints (approve / reject / retry) — the conversation
  // only asks to be told about the retried run afterwards (TICKET-TO-EMPLOYEE-E2E-001).
  const handleApproveRun = (runId) => runAction(() => agentRunsApi.approve(runId), "שגיאה באישור הריצה");
  const handleRejectRun = (runId, reason) => runAction(async () => {
    await agentRunsApi.reject(runId, reason);
    setRejectFor(null);
    setRejectReason("");
  }, "שגיאה בדחיית הריצה");
  const handleRetryRun = (runId) => runAction(async () => {
    const fresh = await agentRunsApi.retry(runId);
    if (fresh?.id) await conversationsApi.trackRun(conversationId, fresh.id);
  }, "שגיאה בניסיון החוזר");

  // Newest trace per run wins: this is the stage the operator was last told about.
  const runStages = new Map();
  for (const m of messages) {
    if (m.message_type === "technical_trace" && m.agent_run_id && m.technical_trace) {
      const t = m.technical_trace;
      runStages.set(m.agent_run_id, {
        stage: t.run_stage || (t.action_status === "PROPOSED" ? "awaiting_technician_review" : null),
        label: t.run_stage_label || STAGE_LABELS[t.run_stage] || null,
        actionStatus: t.action_status,
      });
    }
  }
  const awaitingRunIds = [...runStages].filter(([, s]) => AWAITING_STAGES.has(s.stage)).map(([id]) => id);
  const failedRunIds = [...runStages].filter(([, s]) => s.stage === "failed").map(([id]) => id);
  const hasActiveRun = [...runStages.values()].some((s) => s.stage && !TERMINAL_STAGES.has(s.stage) && !AWAITING_STAGES.has(s.stage));

  // While an employee is working, keep the conversation live (bounded like AgentRunPanel's polling).
  useEffect(() => {
    if (!conversationId || !hasActiveRun) return undefined;
    const timer = setInterval(() => loadMessages(conversationId), POLL_MS);
    return () => clearInterval(timer);
  }, [conversationId, hasActiveRun, loadMessages]);

  // The reply and its runtime trace are two rows in the store but one message on screen.
  const rows = pairConversationTraces(messages);

  if (!canView) return null;

  return (
    <div className="flex flex-col gap-3">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      <div className="flex flex-col gap-2 max-h-[28rem] overflow-y-auto px-1 py-2">
        {rows.length === 0 && (
          <p className="text-gray-400 text-sm italic text-center">אין הודעות עדיין — התחל שיחה למטה</p>
        )}
        {rows.map(({ message: m, trace }) => (
          <MessageBubble
            key={m.id}
            message={m}
            canManage={canManage}
            trace={trace}
            participants={participants}
            author={participants.find((p) => p.id === m.author_participant_id) || null}
          />
        ))}
      </div>

      {runStages.size > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-2">
          {[...runStages].map(([runId, s]) => (
            <div key={runId} className="flex flex-wrap items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded-full ${
                s.stage === "failed" || s.stage === "rejected" ? "bg-red-100 text-red-700"
                : TERMINAL_STAGES.has(s.stage) ? "bg-green-100 text-green-700"
                : AWAITING_STAGES.has(s.stage) ? "bg-amber-100 text-amber-700"
                : "bg-violet-100 text-violet-700"
              }`}>
                {s.label || s.stage || "משימה"}
              </span>
              <a href={`/agent-runs/${runId}`} className="text-gray-400 hover:text-gray-600">משימה {String(runId).slice(0, 8)}</a>
              {canApprove && awaitingRunIds.includes(runId) && (
                <>
                  <button
                    type="button"
                    onClick={() => handleApproveRun(runId)}
                    disabled={loading}
                    className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    אשר משימה ({String(runId).slice(0, 8)})
                  </button>
                  {rejectFor === runId ? (
                    <span className="flex items-center gap-1">
                      <input
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="סיבת הדחייה"
                        className="border border-gray-200 rounded px-2 py-1"
                      />
                      <button
                        type="button"
                        onClick={() => handleRejectRun(runId, rejectReason.trim())}
                        disabled={loading || !rejectReason.trim()}
                        className="px-2 py-1 rounded bg-red-600 text-white disabled:opacity-50"
                      >
                        דחה
                      </button>
                      <button type="button" onClick={() => setRejectFor(null)} className="text-gray-400">ביטול</button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRejectFor(runId)}
                      disabled={loading}
                      className="px-3 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      דחה
                    </button>
                  )}
                </>
              )}
              {canRun && failedRunIds.includes(runId) && (
                <button
                  type="button"
                  onClick={() => handleRetryRun(runId)}
                  disabled={loading}
                  className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  נסה שוב
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canRun && (
        <div className="border-t border-gray-100 pt-3 flex flex-col gap-2">
          {!fixedAgentId && !conversationId && (
            <select
              value={effectiveAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="text-sm border border-gray-200 rounded px-2 py-1.5 w-fit"
            >
              <option value="">בחר עובד דיגיטלי (Digital Employee)...</option>
              {selectableAgents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          )}
          <select
            value={turnType}
            onChange={(e) => setTurnType(e.target.value)}
            className="text-sm border border-gray-200 rounded px-2 py-1.5 w-fit"
          >
            {TURN_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="כתוב הודעה לסוכן... למשל: תבדוק את ESET על LAPTOP-01"
              rows={2}
              className="flex-1 text-sm border border-gray-200 rounded px-3 py-2 resize-none"
              disabled={loading}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={loading || !draft.trim()}
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 self-end"
            >
              שלח
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
